import React, { useMemo } from 'react'
import { Box, Paper, Typography, Chip, Tooltip, Fade, useTheme, alpha } from '@mui/material'
import { FiExternalLink, FiSearch, FiCheckCircle, FiGlobe } from 'react-icons/fi'
import DOMPurify from 'dompurify'
import './GroundingComponents.css'

/**
 * Processes grounding metadata to add inline citations to content
 * @param {string} content - The message content
 * @param {object} metadata - Grounding metadata containing grounding_supports and grounding_chunks
 * @returns {string} HTML string with citations added
 */
export const processGroundingCitations = (content, metadata) => {
  if (!metadata?.grounding_supports?.length || !metadata?.grounding_chunks?.length) {
    return content
  }

  // Sort by end_index descending to avoid position shifts when inserting citations
  const supports = [...metadata.grounding_supports].sort(
    (a, b) => b.segment.end_index - a.segment.end_index
  )

  let result = content

  supports.forEach((support) => {
    if (!support.grounding_chunk_indices?.length) return

    // Generate citation links
    const citations = support.grounding_chunk_indices
      .map((idx) => {
        const chunk = metadata.grounding_chunks[idx]
        if (!chunk?.web) return ''
        
        // Create a citation marker with data attributes for hover effects
        return `<a href="${chunk.web.uri}" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   class="grounding-citation" 
                   data-citation-index="${idx + 1}"
                   data-citation-title="${(chunk.web.title || chunk.web.uri).replace(/"/g, '&quot;')}"
                   aria-label="Source ${idx + 1}: ${chunk.web.title || chunk.web.uri}"
                   >[${idx + 1}]</a>`
      })
      .filter(Boolean)
      .join('')

    if (citations && support.segment.end_index <= result.length) {
      // Insert citations at the end of the segment
      result =
        result.slice(0, support.segment.end_index) +
        citations +
        result.slice(support.segment.end_index)
    }
  })

  return result
}

/**
 * Badge component to indicate web search was used
 */
export const GroundingBadge = ({ metadata }) => {
  const theme = useTheme()

  if (!metadata?.grounding_chunks?.length) return null

  return (
    <Tooltip 
      title={`Verified with ${metadata.grounding_chunks.length} web source${metadata.grounding_chunks.length > 1 ? 's' : ''}`}
      placement="top"
      arrow
    >
      <Chip
        icon={<FiCheckCircle size={14} />}
        label="Web Verified"
        size="small"
        className="grounding-badge"
        sx={{
          height: 24,
          fontSize: '0.7rem',
          fontWeight: 700,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.2)} 0%, ${alpha(theme.palette.info.light, 0.2)} 100%)`,
          border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
          color: theme.palette.success.dark,
          animation: 'pulseGlow 2s ease-in-out infinite',
          '& .MuiChip-icon': {
            fontSize: '14px',
            color: theme.palette.success.main,
          },
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.3)} 0%, ${alpha(theme.palette.info.light, 0.3)} 100%)`,
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
          },
          transition: 'all 0.3s ease',
        }}
      />
    </Tooltip>
  )
}

/**
 * Display search sources at the bottom of the response
 */
export const SearchSources = ({ metadata }) => {
  const theme = useTheme()

  if (!metadata?.grounding_chunks?.length) return null

  return (
    <Fade in timeout={600}>
      <Paper
        elevation={0}
        className="search-sources-container"
        sx={{
          mt: 2,
          p: 2.5,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.info.main, 0.03)} 100%)`,
          border: `1.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.info.main} 50%, ${theme.palette.success.main} 100%)`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
          <FiGlobe 
            size={18} 
            style={{ 
              color: theme.palette.primary.main,
              animation: 'spin 3s linear infinite',
            }} 
          />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: '0.85rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Sources
          </Typography>
          <Chip 
            label={metadata.grounding_chunks.length} 
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 700,
              backgroundColor: alpha(theme.palette.primary.main, 0.15),
              color: theme.palette.primary.main,
            }}
          />
        </Box>
        <Box 
          component="ul" 
          sx={{ 
            m: 0, 
            p: 0, 
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {metadata.grounding_chunks.map((chunk, idx) => (
            <Fade in timeout={800 + idx * 100} key={idx}>
              <Box
                component="li"
                className="source-item"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.6),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    transform: 'translateX(4px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                  },
                }}
              >
                <Chip
                  label={idx + 1}
                  size="small"
                  sx={{
                    minWidth: 28,
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    color: theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="a"
                    href={chunk.web.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: theme.palette.primary.main,
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.5,
                      '&:hover': {
                        textDecoration: 'underline',
                        color: theme.palette.primary.dark,
                      },
                    }}
                  >
                    {chunk.web.title || 'Untitled Source'}
                    <FiExternalLink size={12} style={{ flexShrink: 0 }} />
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                      display: 'block',
                    }}
                  >
                    {new URL(chunk.web.uri).hostname}
                  </Typography>
                </Box>
              </Box>
            </Fade>
          ))}
        </Box>
      </Paper>
    </Fade>
  )
}

/**
 * Display search suggestions/entry point if provided
 */
export const SearchSuggestions = ({ metadata }) => {
  const theme = useTheme()

  const sanitizedContent = useMemo(() => {
    if (!metadata?.search_entry_point?.rendered_content) return null
    
    return DOMPurify.sanitize(metadata.search_entry_point.rendered_content, {
      ALLOWED_TAGS: ['a', 'div', 'span', 'p', 'strong', 'em', 'br'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    })
  }, [metadata])

  if (!sanitizedContent) return null

  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        className="search-suggestions-container"
        sx={{
          mt: 2,
          p: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          borderLeft: `4px solid ${theme.palette.info.main}`,
          borderRadius: 2,
          '& a': {
            color: theme.palette.info.main,
            textDecoration: 'none',
            fontWeight: 600,
            '&:hover': {
              textDecoration: 'underline',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
          <FiSearch size={14} style={{ color: theme.palette.info.main }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: theme.palette.info.main,
              textTransform: 'uppercase',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            Related Searches
          </Typography>
        </Box>
        <Box
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          sx={{
            fontSize: '0.85rem',
            color: theme.palette.text.secondary,
          }}
        />
      </Paper>
    </Fade>
  )
}

/**
 * Display web search queries used
 */
export const SearchQueriesDisplay = ({ metadata }) => {
  const theme = useTheme()

  if (!metadata?.web_search_queries?.length) return null

  return (
    <Box 
      sx={{ 
        mt: 1.5, 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 0.75,
        alignItems: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.7rem',
          fontWeight: 600,
        }}
      >
        Searched:
      </Typography>
      {metadata.web_search_queries.map((query, idx) => (
        <Chip
          key={idx}
          label={query}
          size="small"
          icon={<FiSearch size={10} />}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            color: theme.palette.info.main,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            '& .MuiChip-icon': {
              fontSize: '10px',
              color: theme.palette.info.main,
            },
          }}
        />
      ))}
    </Box>
  )
}

